<template>
  <div>
    <button @click="$emit('exit')" class="text-sm text-gray-500 hover:text-gray-700 mb-4">&larr; All games</button>

    <div v-if="loading" class="text-center py-16 text-gray-500">Loading words…</div>
    <div v-else-if="error" class="text-center py-16 text-red-500">{{ error }}</div>

    <!-- Game over -->
    <div v-else-if="finished" class="bg-white rounded-3xl border border-gray-200 shadow-sm p-8 text-center">
      <div class="text-4xl mb-2">🌧️</div>
      <h2 class="text-2xl font-bold text-gray-900">Game over — {{ correct }} caught</h2>
      <p class="mt-1 text-gray-600">Score <span class="font-bold text-emerald-600">{{ score }}</span>
        <span v-if="bestScore"> · Best {{ bestScore }}</span></p>
      <p class="mt-1 text-emerald-600 font-medium">+{{ xpEarned }} XP<span v-if="xpEarned === 0" class="text-gray-400"> (daily cap reached)</span></p>
      <div class="mt-6 flex gap-3 justify-center">
        <button @click="start" class="bg-emerald-600 text-white font-medium px-6 py-3 rounded-xl hover:bg-emerald-700">Play again</button>
        <button @click="$emit('exit')" class="bg-gray-100 text-gray-700 font-medium px-6 py-3 rounded-xl hover:bg-gray-200">Done</button>
      </div>
    </div>

    <!-- Playing -->
    <div v-else class="bg-white rounded-3xl border border-gray-200 shadow-sm p-6 sm:p-8">
      <div class="flex items-center justify-between text-xs font-medium text-gray-400">
        <span>🌧️ Word Fall</span>
        <span>{{ '❤️'.repeat(lives) }} · {{ score }}</span>
      </div>
      <p class="text-sm text-gray-500 mt-3 mb-3">Tap the English meaning before the word lands.</p>

      <div class="relative bg-gradient-to-b from-emerald-50 to-white border border-gray-200 rounded-2xl overflow-hidden" style="height: 300px;">
        <div v-if="word" class="absolute left-1/2 -translate-x-1/2 transition-none" :style="{ top: `${y}%` }">
          <span class="inline-block bg-white border border-emerald-200 shadow-sm rounded-xl px-4 py-2 text-xl font-bold text-gray-900">
            {{ word.word_vietnamese }}
          </span>
        </div>
      </div>

      <div class="grid grid-cols-2 gap-3 mt-3">
        <button
          v-for="(opt, i) in options" :key="i"
          @click="choose(opt)"
          class="px-4 py-3.5 rounded-xl border-2 border-gray-200 bg-white font-medium text-gray-800 hover:border-emerald-400 transition-all"
        >{{ opt }}</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { apiGet, apiPost } from '../../api/client'

defineEmits(['exit'])

const TICK_MS = 60
const loading = ref(true)
const error = ref(null)
const pool = ref([])
const word = ref(null)
const options = ref([])
const y = ref(0)
const lives = ref(3)
const score = ref(0)
const correct = ref(0)
const attempts = ref(0)
const combo = ref(0)
const finished = ref(false)
const xpEarned = ref(0)
const bestScore = ref(null)
let timer = null

const tz = Intl.DateTimeFormat().resolvedOptions().timeZone

function shuffle(arr) { return [...arr].sort(() => Math.random() - 0.5) }
function fallStep() { return 1.1 + Math.min(2.0, correct.value * 0.12) } // escalates as you catch more

function spawn() {
  const w = pool.value[Math.floor(Math.random() * pool.value.length)]
  word.value = w
  const distractors = shuffle(pool.value.filter(p => p.id !== w.id)).slice(0, 3).map(p => p.word_english)
  options.value = shuffle([w.word_english, ...distractors])
  y.value = 0
}

function tick() {
  if (finished.value || !word.value) return
  y.value += fallStep()
  if (y.value >= 92) miss()
}

function miss() {
  attempts.value += 1
  combo.value = 0
  lives.value -= 1
  if (lives.value <= 0) return gameOver()
  spawn()
}

function choose(opt) {
  if (!word.value || finished.value) return
  attempts.value += 1
  if (opt === word.value.word_english) {
    correct.value += 1
    combo.value += 1
    score.value += 10 + Math.round((100 - y.value) / 5) + combo.value * 5
    spawn()
  } else {
    combo.value = 0
    lives.value -= 1
    if (lives.value <= 0) return gameOver()
    spawn()
  }
}

async function gameOver() {
  finished.value = true
  word.value = null
  clearInterval(timer)
  try {
    const res = await apiPost('/api/v1/games/record', {
      game: 'word_fall', score: score.value, correct: correct.value, total: attempts.value, timezone: tz,
    })
    xpEarned.value = res?.xp_earned ?? 0
    bestScore.value = res?.best_score ?? null
  } catch (e) { /* results still shown */ }
}

async function ensurePool() {
  if (pool.value.length >= 4) return true
  const data = await apiGet('/api/v1/games/vocab_pool?count=20')
  pool.value = data.words || []
  return pool.value.length >= 4
}

async function start() {
  loading.value = true
  error.value = null
  try {
    if (!(await ensurePool())) { error.value = 'Practice a few lessons first to unlock Word Fall!'; return }
  } catch (e) {
    error.value = 'Could not load Word Fall.'
    return
  } finally {
    loading.value = false
  }
  finished.value = false
  lives.value = 3
  score.value = 0
  correct.value = 0
  attempts.value = 0
  combo.value = 0
  spawn()
  clearInterval(timer)
  timer = setInterval(tick, TICK_MS)
}

onMounted(start)
onUnmounted(() => clearInterval(timer))
</script>
